import { useEffect, useRef, useState, type ReactNode } from "react";
import { PMIcon, PMSegmented } from "@/ds/vendor";
import type { Team } from "@/data/queries";
import { applyTheme, getTheme, type Theme } from "./theme";

export function UserMenu({ children, teams, teamId, email, onSwitch, onSignOut }: { children: ReactNode; teams: Team[]; teamId: string; email: string; onSwitch: (id: string) => void; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(getTheme());
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    addEventListener("mousedown", off); addEventListener("keydown", esc);
    return () => { removeEventListener("mousedown", off); removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div className="usermenu" ref={ref}>
      <button className="usermenu-trigger" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{children}</button>
      {open && (
        <div className="usermenu-pop" role="menu">
          <div className="usermenu-email">{email}</div>
          {teams.length > 1 && (
            <>
              <div className="usermenu-label">Teams</div>
              {teams.map((t) => (
                <button key={t.id} role="menuitemradio" aria-checked={t.id === teamId} className="usermenu-item" onClick={() => { setOpen(false); onSwitch(t.id); }}>
                  <span>{t.name}</span>{t.id === teamId && <PMIcon name="check" size={16} />}
                </button>
              ))}
            </>
          )}
          <div className="usermenu-label">Theme</div>
          <PMSegmented value={theme} onChange={(v: string) => { setTheme(v as Theme); applyTheme(v as Theme); }} options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }, { value: "system", label: "Auto" }]} />
          <button role="menuitem" className="usermenu-item usermenu-signout" onClick={onSignOut}>Sign out</button>
        </div>
      )}
    </div>
  );
}
